import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import '../../styles/admin-dashboard.css';
import { IcoPlus, IcoCheck } from '../dashboard/DashIcons';

function AddEmployee() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');
  const [departments, setDepartments]       = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [managers, setManagers]             = useState([]);
  const [selectedManagers, setSelectedManagers]       = useState([]);

  const isManager = user?.role === 'manager';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName:  '',
    email:     '',
    role:      'employee',
    employmentType: '',
    hourlyRate: '0',
    startDate: new Date().toISOString().split('T')[0],
  });

  const token         = user?.token;
  const authHeaders   = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(`/api/departments?organizationId=${user.organizationId}`, { headers: authHeaders });
        if (res.ok) setDepartments(await res.json());
      } catch (e) { console.error(e); }
    };

    const fetchManagers = async () => {
      try {
        const res = await fetch('/api/staff?role=manager', { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.employees || [];
          setManagers(list.filter(m => m.role === 'manager'));
        }
      } catch (e) { console.error(e); }
    };

    fetchDepartments();
    fetchManagers();

    // Pre-select self if manager
    if (isManager) setSelectedManagers([user.id]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleManager = (id) => {
    setSelectedManagers(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body = {
        ...formData,
        hourlyRate:    parseFloat(formData.hourlyRate) || 0,
        organizationId: user.organizationId,
        departmentIds: selectedDepartments,
        managerIds:    selectedManagers.length > 0 ? selectedManagers : (isManager ? [user.id] : []),
      };

      const res  = await fetch('/api/employees', {
        method:  'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });

      const data = await res.json();
      if (res.ok) {
        navigate(`/employee/${data.employee._id}`);
      } else {
        setError(data.message || 'Failed to create employee');
      }
    } catch (e) {
      console.error(e);
      setError('Error creating employee');
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    width: '100%', padding: '0.75rem',
    border: '2px solid #e5e7eb', borderRadius: '8px',
    fontSize: '1rem', boxSizing: 'border-box',
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <button onClick={() => navigate('/team')}
          style={{ marginBottom: '1.5rem', padding: '0.5rem 1rem', background: '#9ca3af', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: 'white' }}>
          ← Back to Team
        </button>

        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: '600px' }}>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><IcoPlus size={22} color="#22c55e"/> Add New Employee</h1>

          {isManager && (
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.875rem', color: '#6b7280', background: '#f0f9ff', padding: '0.6rem 0.875rem', borderRadius: '8px', border: '1px solid #bae6fd' }}>
              You are pre-selected as a manager. You can also assign additional managers below. If this employee already exists in the system, they'll be added to your team.
            </p>
          )}

          {error && (
            <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '1.5rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

              {/* First Name */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>First Name *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required style={fieldStyle} />
              </div>

              {/* Last Name */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Last Name *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required style={fieldStyle} />
              </div>

              {/* Email */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Email {['manager', 'adminassistant', 'admin', 'superadmin'].includes(formData.role) ? '*' : '(optional)'}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required={['manager', 'adminassistant', 'superadmin'].includes(formData.role)}
                  style={fieldStyle}
                />
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  {isManager
                    ? 'If this email already exists in the system, the employee will be added to your team.'
                    : ['manager', 'adminassistant', 'superadmin'].includes(formData.role)
                      ? 'Required — managers and admins need an email to log in.'
                      : 'Employees do not need an email unless they will log in to the app.'}
                </p>
              </div>

              {/* Role */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Role *</label>
                <select name="role" value={formData.role} onChange={handleChange} style={fieldStyle}>
                  <option value="employee">Employee</option>
                  <option value="teamlead">Team Lead</option>
                  {!isManager && <option value="manager">Manager</option>}
                  {!isManager && <option value="adminassistant">Assistant Admin</option>}
                  {!isManager && <option value="admin">Admin</option>}
                  {!isManager && <option value="superadmin">Super Admin</option>}
                </select>
              </div>

              {/* Employment Type */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Employment Type</label>
                <select name="employmentType" value={formData.employmentType} onChange={handleChange} style={fieldStyle}>
                  <option value="">Select one</option>
                  <option value="fulltime">Full-time</option>
                  <option value="parttime">Part-time</option>
                  <option value="salaried">Salaried</option>
                </select>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  Used to determine PTO &amp; sick time allocation.
                </p>
              </div>

              {/* Assign Managers */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Assign Managers
                  {selectedManagers.length > 0 && (
                    <span style={{ marginLeft: '0.5rem', color: '#22c55e', fontWeight: '600', fontSize: '0.8rem' }}>
                      • {selectedManagers.length} selected
                    </span>
                  )}
                </label>
                {managers.length === 0 ? (
                  <p style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                    No managers found in your organization.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {managers.map(m => {
                      const selected = selectedManagers.includes(m._id);
                      const isSelf   = m._id === user.id;
                      return (
                        <button
                          key={m._id}
                          type="button"
                          onClick={() => !isSelf && toggleManager(m._id)}
                          style={{
                            padding: '0.35rem 0.75rem',
                            borderRadius: '99px',
                            border: selected ? '2px solid #22c55e' : '2px solid #e5e7eb',
                            background: selected ? '#ede9fe' : '#f9fafb',
                            color: selected ? '#4c1d95' : '#374151',
                            fontSize: '0.8rem', fontWeight: '600',
                            cursor: isSelf ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                          }}
                        >
                          {selected && <IcoCheck size={12} color="#22c55e"/>}
                          {m.firstName} {m.lastName}
                          {isSelf && <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>(you)</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Departments */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Departments (Optional)</label>
                {departments.length === 0 ? (
                  <p style={{ padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                    No departments yet.{' '}
                    <button type="button" onClick={() => navigate('/setup-departments')}
                      style={{ background: 'none', border: 'none', color: '#d97706', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.875rem' }}>
                      Create one first
                    </button>
                  </p>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {departments.map(dept => {
                        const selected = selectedDepartments.includes(dept._id);
                        return (
                          <button
                            key={dept._id}
                            type="button"
                            onClick={() => setSelectedDepartments(prev =>
                              prev.includes(dept._id) ? prev.filter(d => d !== dept._id) : [...prev, dept._id]
                            )}
                            style={{
                              padding: '0.35rem 0.75rem', borderRadius: '99px',
                              border: selected ? `2px solid ${dept.color}` : '2px solid #e5e7eb',
                              background: selected ? dept.color : '#f9fafb',
                              color: selected ? 'white' : '#374151',
                              fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '0.35rem',
                            }}
                          >
                            {selected && <IcoCheck size={12} color="#22c55e"/>}
                            {dept.name}
                          </button>
                        );
                      })}
                    </div>
                    {selectedDepartments.length > 0 && (
                      <p style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: '600', margin: '0.4rem 0 0' }}>
                        {selectedDepartments.length} department{selectedDepartments.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Hourly Rate */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Hourly Rate ($)</label>
                <input type="number" name="hourlyRate" value={formData.hourlyRate} onChange={handleChange} min="0" step="0.01" style={fieldStyle} />
              </div>

              {/* Start Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Start Date</label>
                <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} style={fieldStyle} />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => navigate('/team')}
                  style={{ flex: 1, padding: '0.75rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}
                  style={{ flex: 1, padding: '0.75rem', background: loading ? '#9ca3af' : 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                  {loading ? 'Saving...' : isManager ? 'Add to Team' : 'Create Employee'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddEmployee;
